import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  FileText,
  User,
  Mail,
  Phone,
  Calendar,
  Download,
  Eye,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Filter,
  RefreshCw,
  Loader2,
  Image,
  FileCheck,
  Users
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminStandardQueue = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [expandedCase, setExpandedCase] = useState(null);

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/cases/standard-pending`);
      setCases(res.data.cases || []);
    } catch (error) {
      console.error("Error fetching cases:", error);
      toast.error("Error al cargar los casos");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkProcessed = async (caseId) => {
    try {
      setProcessing(caseId);
      await axios.post(`${API}/admin/cases/${caseId}/mark-processed`);
      toast.success("Caso marcado como procesado");
      fetchCases();
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Error al procesar");
    } finally {
      setProcessing(null);
    }
  };

  const toggleExpand = (caseId) => {
    setExpandedCase(expandedCase === caseId ? null : caseId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900">
            Cola de Casos Standard
          </h1>
          <p className="text-navy-500">
            {cases.length} caso{cases.length !== 1 ? "s" : ""} pendiente{cases.length !== 1 ? "s" : ""} de procesar
          </p>
        </div>
        <Button onClick={fetchCases} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Cases List */}
      {cases.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-2xl">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-navy-800 mb-2">
            No hay casos pendientes
          </h2>
          <p className="text-navy-500">
            Todos los casos Standard han sido procesados
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {cases.map((caseItem) => (
            <motion.div
              key={caseItem.case_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-navy-100 overflow-hidden"
            >
              {/* Case Header */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleExpand(caseItem.case_id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <User className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy-900">
                        {caseItem.client?.name || "Sin nombre"}
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-navy-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {caseItem.client?.email}
                        </span>
                        {caseItem.client?.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {caseItem.client.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Stats */}
                    <div className="flex items-center gap-3 text-sm">
                      <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 rounded-full text-blue-700">
                        <FileText className="w-4 h-4" />
                        <span>{caseItem.documents_count} docs</span>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 rounded-full text-purple-700">
                        <FileCheck className="w-4 h-4" />
                        <span>{caseItem.ds160_sections_completed}/12 DS-160</span>
                      </div>
                      {caseItem.family_count > 0 && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 rounded-full text-amber-700">
                          <Users className="w-4 h-4" />
                          <span>{caseItem.family_count + 1} personas</span>
                        </div>
                      )}
                    </div>

                    {/* Submitted Date */}
                    <div className="text-sm text-navy-500 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {new Date(caseItem.submitted_at).toLocaleDateString("es-ES", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </div>

                    {/* Expand Icon */}
                    {expandedCase === caseItem.case_id ? (
                      <ChevronUp className="w-5 h-5 text-navy-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-navy-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedCase === caseItem.case_id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-navy-100"
                >
                  <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Documents */}
                    <div>
                      <h4 className="font-semibold text-navy-800 mb-3 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-500" />
                        Documentos ({caseItem.documents?.length || 0})
                      </h4>
                      <div className="space-y-2">
                        {caseItem.documents?.map((doc, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              {doc.type?.includes("photo") || doc.type?.includes("foto") ? (
                                <Image className="w-4 h-4 text-purple-500" />
                              ) : (
                                <FileText className="w-4 h-4 text-blue-500" />
                              )}
                              <div>
                                <p className="text-sm font-medium text-navy-800">
                                  {doc.type || "Documento"}
                                </p>
                                <p className="text-xs text-navy-500">{doc.filename}</p>
                              </div>
                            </div>
                            {doc.url && (
                              <a
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        ))}
                        {(!caseItem.documents || caseItem.documents.length === 0) && (
                          <p className="text-sm text-gray-500 italic">Sin documentos</p>
                        )}
                      </div>
                    </div>

                    {/* DS-160 Data */}
                    <div>
                      <h4 className="font-semibold text-navy-800 mb-3 flex items-center gap-2">
                        <FileCheck className="w-5 h-5 text-purple-500" />
                        Datos DS-160
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm max-h-80 overflow-y-auto">
                        {caseItem.ds160_data?.surname && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Apellido:</span>
                            <span className="font-medium text-navy-800">{caseItem.ds160_data.surname}</span>
                          </div>
                        )}
                        {caseItem.ds160_data?.given_names && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Nombres:</span>
                            <span className="font-medium text-navy-800">{caseItem.ds160_data.given_names}</span>
                          </div>
                        )}
                        {caseItem.ds160_data?.birth_date && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Fecha Nac:</span>
                            <span className="font-medium text-navy-800">{caseItem.ds160_data.birth_date}</span>
                          </div>
                        )}
                        {caseItem.ds160_data?.birth_city && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Ciudad Nac:</span>
                            <span className="font-medium text-navy-800">{caseItem.ds160_data.birth_city}</span>
                          </div>
                        )}
                        {caseItem.ds160_data?.nationality && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Nacionalidad:</span>
                            <span className="font-medium text-navy-800">{caseItem.ds160_data.nationality}</span>
                          </div>
                        )}
                        {caseItem.ds160_data?.passport_number && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Pasaporte:</span>
                            <span className="font-medium text-navy-800">{caseItem.ds160_data.passport_number}</span>
                          </div>
                        )}
                        {caseItem.ds160_data?.email && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Email:</span>
                            <span className="font-medium text-navy-800">{caseItem.ds160_data.email}</span>
                          </div>
                        )}
                        {caseItem.ds160_data?.phone && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Teléfono:</span>
                            <span className="font-medium text-navy-800">{caseItem.ds160_data.phone}</span>
                          </div>
                        )}
                        {caseItem.ds160_data?.address && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Dirección:</span>
                            <span className="font-medium text-navy-800 text-right max-w-[60%]">{caseItem.ds160_data.address}</span>
                          </div>
                        )}
                        {caseItem.ds160_data?.occupation && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Ocupación:</span>
                            <span className="font-medium text-navy-800">{caseItem.ds160_data.occupation}</span>
                          </div>
                        )}
                        {caseItem.ds160_data?.employer_name && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Empleador:</span>
                            <span className="font-medium text-navy-800">{caseItem.ds160_data.employer_name}</span>
                          </div>
                        )}
                        {caseItem.ds160_data?.education_level && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Educación:</span>
                            <span className="font-medium text-navy-800">{caseItem.ds160_data.education_level}</span>
                          </div>
                        )}
                        {caseItem.ds160_data?.travel_purpose && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Propósito Viaje:</span>
                            <span className="font-medium text-navy-800">{caseItem.ds160_data.travel_purpose}</span>
                          </div>
                        )}
                        {caseItem.ds160_data?.us_contact_name && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Contacto USA:</span>
                            <span className="font-medium text-navy-800">{caseItem.ds160_data.us_contact_name}</span>
                          </div>
                        )}
                        
                        {/* Show raw data link */}
                        <details className="mt-4 border-t pt-3">
                          <summary className="cursor-pointer text-blue-600 hover:text-blue-700 text-xs">
                            Ver todos los datos (JSON)
                          </summary>
                          <pre className="mt-2 text-xs bg-navy-800 text-green-400 p-3 rounded overflow-x-auto max-h-60">
                            {JSON.stringify(caseItem.ds160_data, null, 2)}
                          </pre>
                        </details>
                      </div>
                    </div>
                  </div>

                  {/* Family Members */}
                  {caseItem.family_members?.length > 0 && (
                    <div className="px-6 pb-4">
                      <h4 className="font-semibold text-navy-800 mb-3 flex items-center gap-2">
                        <Users className="w-5 h-5 text-amber-500" />
                        Familiares ({caseItem.family_members.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {caseItem.family_members.map((member, idx) => (
                          <div
                            key={idx}
                            className="px-3 py-2 bg-amber-50 rounded-lg text-sm"
                          >
                            <span className="font-medium text-amber-800">{member.name}</span>
                            <span className="text-amber-600 ml-2">({member.relationship})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="px-6 pb-6 flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => window.open(`/admin/case/${caseItem.case_id}`, "_blank")}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Caso Completo
                    </Button>
                    <Button
                      onClick={() => handleMarkProcessed(caseItem.case_id)}
                      disabled={processing === caseItem.case_id}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {processing === caseItem.case_id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                      )}
                      Marcar como Procesado
                    </Button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminStandardQueue;
